/**
 * Owner: Masa Bayu
 * File: private-author-view.ts
 * Date:Time: 2026-10-23 07:35
 * Description: §467-C — model paparan dashboard author PRIVAT, dibentuk HANYA
 *   daripada `GET /v1/publisher/dashboard` (qxp-hub §467-B). Ganti kepada
 *   `private-author-data.ts`: tiada angka rekaan. Konsep yang TIADA sumber di hub
 *   (royalti, pengikut, rating, pencapaian, format jualan, ulasan, notifikasi
 *   promosi) dipulangkan `null` supaya pelukis paparkan "tiada data" — bukan
 *   nilai cantuman dan bukan lencana DEMO.
 */

import { formatMarketplacePriceOrUnknown } from '../../home/marketplace/marketplace-locale';
import type {
  HubBookSales,
  HubMoney,
  HubOrderRow,
  HubPublisherBook,
  PublisherDashboardView,
} from './publisher-dashboard';
import { STUDIO_ENTRY } from '../author-production-identity';
import { slugifyAuthorName } from '../public/public-author-data';
import type { PrivateAuthorLabels, PrivateShortcutId } from './private-author-labels';

/** Nilai yang tiada sumber di pelayan — dilukis sebagai en-dash, bukan 0. */
export const NO_DATA = '—';

/** Tone latar cover (hiasan sahaja, bukan data) — deterministik ikut id. */
export function coverToneOf(publicationId: string): number {
  let sum = 0;
  for (const ch of publicationId) sum = (sum + (ch.codePointAt(0) ?? 0)) % 997;
  return sum;
}

export interface AuthorMoneyText {
  readonly currency: string;
  readonly minorUnits: number;
  readonly text: string;
}

export interface AuthorKpi {
  readonly id: 'sales' | 'royalty' | 'books' | 'followers';
  readonly value: string;
  /** null = perubahan tidak boleh dikira dari data sebenar → tiada lencana. */
  readonly deltaPct: number | null;
  readonly real: boolean;
}

export interface AuthorSeriesPoint {
  readonly month: string;
  readonly label: string;
  readonly minorUnits: number;
  readonly orders: number;
}

export interface AuthorBookRow {
  readonly publicationId: string;
  readonly title: string;
  readonly revision: number | null;
  readonly publishedAt: string | null;
  readonly priceText: string;
  readonly listed: boolean;
  readonly category: string | null;
  readonly coverTone: number;
  readonly soldOrders: number;
  readonly soldBuyers: number;
  readonly revenueText: string;
}

export interface AuthorOrderRow {
  readonly orderId: string;
  readonly title: string;
  readonly state: string;
  readonly createdAt: string | null;
  readonly amountText: string;
}

/** Notifikasi = fakta pelayan yang perlu tindakan; tiada mesej rekaan. */
export type AuthorAlertKind = 'pending' | 'unpriced' | 'newPaid';

export interface AuthorAlert {
  readonly id: string;
  readonly kind: AuthorAlertKind;
  readonly count: number;
}

/** Sasaran + ikon notifikasi — satu pemetaan untuk rail desktop dan bar mudah alih. */
export const ALERT_ACTIONS: Readonly<
  Record<
    AuthorAlertKind,
    { readonly icon: 'clock' | 'card' | 'checkCircle'; readonly target: string }
  >
> = Object.freeze({
  pending: Object.freeze({ icon: 'clock', target: '/author/earnings' }),
  unpriced: Object.freeze({ icon: 'card', target: '/author/publications' }),
  newPaid: Object.freeze({ icon: 'checkCircle', target: '/author/earnings' }),
});

export function alertText(alert: AuthorAlert, la: PrivateAuthorLabels): string {
  const count = String(alert.count);
  if (alert.kind === 'pending') return `${count} ${la.alertPending}`;
  if (alert.kind === 'unpriced') return `${count} ${la.alertUnpriced}`;
  return `${count} ${la.alertPaid}`;
}

export interface AuthorShortcut {
  readonly id: PrivateShortcutId;
  readonly icon: 'upload' | 'bookPlus' | 'megaphone' | 'report' | 'price' | 'receipt';
  readonly target: string;
}

/** Pintasan Pantas — setiap sasaran ialah laluan nyata dalam aplikasi ini. */
export const AUTHOR_SHORTCUTS: readonly AuthorShortcut[] = Object.freeze([
  { id: 'upload', icon: 'upload', target: STUDIO_ENTRY },
  { id: 'bookPlus', icon: 'bookPlus', target: STUDIO_ENTRY },
  { id: 'megaphone', icon: 'megaphone', target: '/author/storefront' },
  { id: 'report', icon: 'report', target: '/author/analytics' },
  { id: 'price', icon: 'price', target: '/author/publications' },
  { id: 'receipt', icon: 'receipt', target: '/author/earnings' },
]);

function sumMinor(rows: readonly HubMoney[]): number {
  return rows.reduce((total, row) => total + row.minorUnits, 0);
}

function moneyText(rows: readonly HubMoney[], priceUnknown: string): string {
  if (rows.length === 0) return NO_DATA;
  return rows
    .map((row) => formatMarketplacePriceOrUnknown(row.minorUnits, row.currency, priceUnknown))
    .join(' · ');
}

/** Mata wang dengan hasil terbesar — siri carta hanya boleh satu skala. */
function primaryCurrency(rows: readonly HubMoney[]): string | null {
  const top = [...rows].sort((a, b) => b.minorUnits - a.minorUnits)[0];
  return top === undefined ? null : top.currency;
}

function minorForCurrency(rows: readonly HubMoney[], currency: string): number | null {
  const hit = rows.find((row) => row.currency === currency);
  return hit === undefined ? null : hit.minorUnits;
}

function monthLabel(month: string, interfaceTag: string): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString(interfaceTag, { month: 'short', timeZone: 'UTC' });
}

function deltaPct(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

export function initialsOf(displayName: string, fallback: string): string {
  const source = displayName.trim().length > 0 ? displayName.trim() : fallback.trim();
  const words = source.split(/[\s@._-]+/).filter((part) => part.length > 0);
  const first = words[0] ?? '';
  const second = words[1] ?? '';
  if (first.length === 0) return 'QX';
  return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
}

export interface AuthorDashboardModel {
  readonly ready: boolean;
  readonly name: string;
  readonly initials: string;
  readonly email: string | null;
  readonly role: string;
  readonly sinceYear: string | null;
  readonly profilePath: string;
  readonly booksPublished: number;
  readonly paidOrders: number;
  readonly pendingOrders: number;
  readonly listedBooks: number;
  readonly buyers: number;
  readonly revenueText: string;
  readonly revenueRows: readonly AuthorMoneyText[];
  readonly monthRevenueText: string;
  readonly kpis: readonly AuthorKpi[];
  readonly currency: string | null;
  readonly series: readonly AuthorSeriesPoint[];
  readonly books: readonly AuthorBookRow[];
  readonly orders: readonly AuthorOrderRow[];
  /** Notifikasi = pesanan yang benar-benar menunggu tindakan (bukan hiasan). */
  readonly alerts: readonly AuthorAlert[];
  readonly alertCount: number;
  readonly noData: string;
}

const EMPTY_MODEL: AuthorDashboardModel = {
  ready: false,
  name: '',
  initials: 'QX',
  email: null,
  role: '',
  sinceYear: null,
  profilePath: '/author/profile',
  booksPublished: 0,
  paidOrders: 0,
  pendingOrders: 0,
  listedBooks: 0,
  buyers: 0,
  revenueText: NO_DATA,
  revenueRows: [],
  monthRevenueText: NO_DATA,
  kpis: [],
  currency: null,
  series: [],
  books: [],
  orders: [],
  alerts: [],
  alertCount: 0,
  noData: NO_DATA,
};

function bookRow(book: HubPublisherBook, priceUnknown: string): AuthorBookRow {
  const sales: HubBookSales = book.sales;
  return {
    publicationId: book.publicationId,
    title: book.title,
    revision: book.revision,
    publishedAt: book.publishedAt,
    priceText: formatMarketplacePriceOrUnknown(book.priceMinorUnits, book.currency, priceUnknown),
    listed: book.priceMinorUnits !== null,
    category: book.category,
    coverTone: coverToneOf(book.publicationId),
    soldOrders: sales.orders,
    soldBuyers: sales.buyers,
    revenueText: moneyText(sales.revenueByCurrency, priceUnknown),
  };
}

function orderRow(row: HubOrderRow, priceUnknown: string): AuthorOrderRow {
  return {
    orderId: row.orderId,
    title: row.title,
    state: row.state ?? 'unknown',
    createdAt: row.createdAt,
    amountText: formatMarketplacePriceOrUnknown(row.amountMinorUnits, row.currency, priceUnknown),
  };
}

/** Notifikasi benar: hanya fakta pelayan yang menunggu tindakan Owner. */
function deriveAlerts(
  pendingOrders: number,
  unpricedBooks: number,
  paidOrders: number,
): readonly AuthorAlert[] {
  const alerts: AuthorAlert[] = [];
  if (pendingOrders > 0) alerts.push({ id: 'pending', kind: 'pending', count: pendingOrders });
  if (unpricedBooks > 0) alerts.push({ id: 'unpriced', kind: 'unpriced', count: unpricedBooks });
  if (paidOrders > 0) alerts.push({ id: 'paid', kind: 'newPaid', count: paidOrders });
  return alerts;
}

/**
 * Bentuk model paparan. `view === null` (anonymous/loading/error) → model kosong
 * dengan `ready:false`; pelukis mesti paparkan keadaan itu, bukan angka 0.
 */
export function buildAuthorDashboardModel(
  view: PublisherDashboardView | null,
  priceUnknown: string,
  interfaceTag: string,
): AuthorDashboardModel {
  if (view === null) return EMPTY_MODEL;
  const { totals, monthly } = view;
  const currency = primaryCurrency(totals.revenueByCurrency);
  const series: readonly AuthorSeriesPoint[] = monthly.map((point) => ({
    month: point.month,
    label: monthLabel(point.month, interfaceTag),
    minorUnits:
      minorForCurrency(point.revenueByCurrency, currency ?? '') ??
      sumMinor(point.revenueByCurrency),
    orders: point.orders,
  }));

  const current = series[series.length - 1] ?? null;
  const previous = series[series.length - 2] ?? null;
  const salesDelta = deltaPct(current?.minorUnits ?? null, previous?.minorUnits ?? null);

  const kpis: readonly AuthorKpi[] = [
    {
      id: 'sales',
      value: moneyText(totals.revenueByCurrency, priceUnknown),
      deltaPct: salesDelta,
      real: true,
    },
    { id: 'royalty', value: NO_DATA, deltaPct: null, real: false },
    { id: 'books', value: String(totals.booksPublished), deltaPct: null, real: true },
    { id: 'followers', value: NO_DATA, deltaPct: null, real: false },
  ];

  const books = view.publications.map((book) => bookRow(book, priceUnknown));
  const unpricedBooks = books.filter((book) => !book.listed).length;
  const alerts = deriveAlerts(totals.pendingOrders, unpricedBooks, totals.paidOrders);
  const name = view.identity.displayName;

  return {
    ready: true,
    name,
    initials: initialsOf(name, view.identity.email ?? ''),
    sinceYear: view.identity.createdAt === null ? null : view.identity.createdAt.slice(0, 4),
    role: view.identity.role,
    email: view.identity.email,
    profilePath: `/author/${encodeURIComponent(slugifyAuthorName(name))}`,
    booksPublished: totals.booksPublished,
    paidOrders: totals.paidOrders,
    pendingOrders: totals.pendingOrders,
    listedBooks: books.length - unpricedBooks,
    buyers: totals.buyers,
    revenueText: moneyText(totals.revenueByCurrency, priceUnknown),
    revenueRows: totals.revenueByCurrency.map((row) => ({
      currency: row.currency,
      minorUnits: row.minorUnits,
      text: formatMarketplacePriceOrUnknown(row.minorUnits, row.currency, priceUnknown),
    })),
    monthRevenueText:
      current === null
        ? NO_DATA
        : formatMarketplacePriceOrUnknown(current.minorUnits, currency, priceUnknown),
    kpis,
    currency,
    series,
    books,
    orders: view.recentOrders.map((row) => orderRow(row, priceUnknown)),
    alerts,
    alertCount: totals.pendingOrders,
    noData: NO_DATA,
  };
}
